import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link, useOutletContext } from 'react-router-dom';
import AddFlashcardGroup from './AddFlashcardGroup';
import Spinner from './Spinner';
import { backendUrl } from '../config';
import './ModulePage.css';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabaseClient';
import { isTopicSafe } from '../utils/safeForAds';
import { getUserTimezone } from '../utils/timezoneUtils';
import { updateCountsAfterWordChange, updateCountsAfterGroupChange } from '../utils/countUpdates';
import { useModuleData } from '../hooks/useModuleData';
import { useData } from '../contexts/DataContext';
import { useIsClassroomHead } from '../hooks/useIsClassroomHead';


const LearnTopicPage = () => {
  const { id } = useParams();
  const { t, isRTL } = useLanguage();
  const { moduleData, loading: moduleLoading } = useModuleData(id);
  const { refreshModules } = useData();
  const isHead = useIsClassroomHead(moduleData?.classroom_id);

  // Determine if user can create word groups/words
  // For private modules (no classroom_id), user can always create
  // For classroom modules, only heads can create
  const canEdit = moduleData?.classroom_id ? (isHead === true) : true;

  const [learnMode, setLearnMode] = useState(null); // 'manual' or 'ai'
  const [topic, setTopic] = useState('');
  const [wordCount, setWordCount] = useState(25);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const requestIdRef = useRef(0);
  const { refetchUsage } = useOutletContext() || {};

  // Redirect if user doesn't have permission (check after data loads)
  useEffect(() => {
    if (!moduleLoading && moduleData) {
      // If it's a classroom module and user is not a head, redirect immediately
      if (moduleData.classroom_id && isHead !== null && !canEdit) {
        navigate(`/module/${moduleData.id}`, { replace: true });
      }
    }
  }, [moduleData, moduleLoading, isHead, canEdit, navigate]);

  // Show loading while checking permissions
  if (moduleLoading || (moduleData?.classroom_id && isHead === null)) {
    return <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>{t('loading') || 'Loading...'}</div>;
  }

  if (!moduleData) return (
    <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
      <h2>{t('moduleNotFound')}</h2>
      <p>{error || t('moduleNotFoundMessage')}</p>
      <Link to="/" className="btn btn-primary">{t('backToModules')}</Link>
    </div>
  );

  // Check if user has permission to create word groups/words
  // If it's a classroom module and user is not a head, show error and redirect
  if (moduleData.classroom_id && !canEdit) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
        <h2>{t('accessDenied') || 'Access Denied'}</h2>
        <p>{t('onlyClassroomHeadCanCreate') || 'Only Teachers and Co-Teachers can create word groups and words in classroom modules.'}</p>
        <button onClick={() => navigate(`/module/${moduleData.id}`)} className="btn btn-primary">
          {t('backToModule') || 'Back to Module'}
        </button>
      </div>
    );
  }

  const handleManualAdd = async (groupName, wordPairs) => {
    setLoading(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) {
        setError(t('pleaseLogIn'));
        setLoading(false);
        return;
      }
      // Get the maximum position for word groups in this module to safely assign next position
      const { data: existingGroups, error: groupsError } = await supabase
        .from('word_groups')
        .select('position')
        .eq('module_id', moduleData.id)
        .order('position', { ascending: false })
        .limit(1);

      if (groupsError) {
        console.error('Error fetching existing groups:', groupsError);
        setError(t('couldNotCreateWordGroup') || 'Could not create word group.');
        setLoading(false);
        return;
      }

      const position = (!existingGroups || existingGroups.length === 0)
        ? 0
        : (existingGroups[0].position + 1);

      // Insert new word group and get its id
      const insertPayload = {
        user_id: userId,
        module_id: moduleData.id,
        name: groupName.trim().substring(0, 100),
        position,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: groupInsert, error: insertError } = await supabase
        .from('word_groups')
        .insert(insertPayload)
        .select()
        .single();

      if (insertError || !groupInsert) {
        console.error('Word group insert error:', insertError, groupInsert);
        setError(t('couldNotCreateWordGroup') || 'Could not create word group.');
        setLoading(false);
        return;
      }

      // Insert words into words table
      if (!groupInsert || !groupInsert.id) {
        setError('Failed to create word group: missing group id.');
        console.error('Group insert failed or missing id:', groupInsert);
        setLoading(false);
        return;
      }

      const fromKey = moduleData.from_lang;
      const toKey = moduleData.to_lang;
      // Convert to { left, right } format
      const normalizedPairs = (wordPairs || []).map(pair => ({
        left: pair.left ?? pair[fromKey],
        right: pair.right ?? pair[toKey]
      }));

      if (normalizedPairs && normalizedPairs.length > 0) {
        // Filter out any invalid pairs
        const validPairs = normalizedPairs.filter(
          pair => pair && typeof pair.left === 'string' && pair.left.trim() && typeof pair.right === 'string' && pair.right.trim()
        );

        if (validPairs.length === 0) {
          setError('No valid word pairs to insert.');
          setLoading(false);
          return;
        }
        const wordsToInsert = validPairs.map((pair, idx) => ({
          group_id: groupInsert.id,
          from_text: pair.left.trim(),
          to_text: pair.right.trim(),
          position: idx,
          // SRS data now stored in word_srs_data table
        }));

        const { data: insertedWords, error: wordsError } = await supabase
          .from('words')
          .insert(wordsToInsert)
          .select();
        if (wordsError) {
          console.error('Supabase insert error:', wordsError);
          setError(t('couldNotCreateWords') || 'Could not create words.');
          setLoading(false);
          return;
        }

        if (!insertedWords || insertedWords.length !== wordsToInsert.length) {
          setError('Not all words were inserted.');
          setLoading(false);
          return;
        }

        // Update counts after word insertion
        await updateCountsAfterWordChange(groupInsert.id);
      } else {
        // If no words were inserted, still update module counts to reflect new word group
        await updateCountsAfterGroupChange(moduleData.id);
      }

      // Refresh dashboard modules to update counts
      refreshModules();

      // If this is a classroom module, refresh classrooms list
      if (moduleData?.classroom_id) {
        window.dispatchEvent(new Event('refreshClassrooms'));
      }

      // After insertion, go to the new word group page
      navigate(`/module/${moduleData.id}/group/${groupInsert.id}`);
    } catch (err) {
      setError(t('couldNotCreateWordGroup') || 'Could not create word group.');
    } finally {
      setLoading(false);
    }
  };

  // Handler for AI-generated words
  const handleAIGeneratedWords = async (topic, aiPairs) => {
    setLoading(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) {
        setError(t('pleaseLogIn'));
        setLoading(false);
        return;
      }
      // Get the maximum position for word groups in this module to safely assign next position
      const { data: existingGroups, error: groupsError } = await supabase
        .from('word_groups')
        .select('position')
        .eq('module_id', moduleData.id)
        .order('position', { ascending: false })
        .limit(1);

      if (groupsError) {
        console.error('Error fetching existing groups:', groupsError);
        setError(t('couldNotCreateWordGroup') || 'Could not create word group.');
        setLoading(false);
        return;
      }

      const position = (!existingGroups || existingGroups.length === 0)
        ? 0
        : (existingGroups[0].position + 1);

      // Insert new word group and get its id
      const { data: groupInsert, error: insertError } = await supabase
        .from('word_groups')
        .insert({
          user_id: userId,
          module_id: moduleData.id,
          name: topic.trim().substring(0, 100), // Store the topic as the group name
          position,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();
      if (insertError || !groupInsert) {
        setError(t('couldNotCreateWordGroup') || 'Could not create word group.');
        setLoading(false);
        return;
      }
      // Normalize AI pairs
      const fromKey = moduleData.from_lang;
      const toKey = moduleData.to_lang;
      const normalizedPairs = (aiPairs || []).map(pair => ({
        left: pair[fromKey],
        right: pair[toKey]
      }));
      const validPairs = normalizedPairs.filter(
        pair => pair && typeof pair.left === 'string' && pair.left.trim() && typeof pair.right === 'string' && pair.right.trim()
      );
      if (validPairs.length === 0) {
        setError('No valid word pairs to insert.');
        setLoading(false);
        return;
      }
      const wordsToInsert = validPairs.map((pair, idx) => ({
        group_id: groupInsert.id,
        from_text: pair.left.trim(),
        to_text: pair.right.trim(),
        position: idx,
        // SRS data now stored in word_srs_data table
      }));
      const { error: wordsError } = await supabase
        .from('words')
        .insert(wordsToInsert)
        .select();
      if (wordsError) {
        setError(t('couldNotCreateWords') || 'Could not create words.');
        setLoading(false);
        return;
      }

      // Update counts after word insertion
      await updateCountsAfterWordChange(groupInsert.id);

      // Refresh dashboard modules to update counts
      refreshModules();

      // If this is a classroom module, refresh classrooms list
      if (moduleData?.classroom_id) {
        window.dispatchEvent(new Event('refreshClassrooms'));
      }

      navigate(`/module/${moduleData.id}/group/${groupInsert.id}`);
    } catch (err) {
      setError(t('couldNotCreateWordGroup') || 'Could not create word group.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Show overlay spinner only during AI/manual add, not during initial load */}
      {loading && moduleData && learnMode && (
        <Spinner
          message={learnMode === 'manual' ? t('creatingWordGroup') : t('generatingWords')}
          size="large"
          overlay
        />
      )}
      <div className="module-page-container" dir={isRTL ? 'rtl' : 'ltr'} style={{ textAlign: isRTL ? 'right' : 'left' }}>
        <button onClick={() => navigate(`/module/${moduleData.id}`)} className="btn btn-secondary btn-back">
          {t('backToModule')}
        </button>
        <div className="module-page-header">
          <h2>{moduleData.from_lang === moduleData.to_lang
            ? t('addWordsManually')
            : `${t('learnATopic')} ${t(moduleData.from_lang)} ${isRTL ? '\u2190' : '\u2192'} ${t(moduleData.to_lang)}`}</h2>
        </div>
        {!learnMode && (
          <div className="card learn-topic-card">
            <h4>{t('howWouldYouLikeToAddWords')}</h4>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button className="btn btn-primary" onClick={() => setLearnMode('manual')}>{t('addWordsManually')}</button>
              {moduleData.from_lang !== moduleData.to_lang && (
                <button className="btn btn-primary" onClick={() => setLearnMode('ai')}>{t('generateWithAI')}</button>
              )}
            </div>
            <button className="btn btn-secondary" style={{ marginTop: '1.5rem' }} onClick={() => navigate(`/module/${moduleData.id}`)}>{t('cancel')}</button>
          </div>
        )}
        {learnMode === 'manual' && (
          <div className="card learn-topic-card">
            <AddFlashcardGroup
              from={moduleData.from_lang}
              to={moduleData.to_lang}
              onAdd={handleManualAdd}
              onCancel={() => setLearnMode(null)}
            />
          </div>
        )}
        {learnMode === 'ai' && (
          <div className="card learn-topic-card">
            <h4>{t('enterTopicToLearn')}</h4>
            <div className="form-group">
              <input
                type="text"
                value={topic}
                onChange={e => setTopic(e.target.value.substring(0, 100))}
                placeholder={t('topicPlaceholder')}
                maxLength={100}
                disabled={loading}
              />
              {topic.length >= 100 && (
                <div style={{
                  color: '#d32f2f',
                  fontSize: '0.8rem',
                  marginTop: '4px'
                }}>
                  {t('topicMaxLength') || 'Topic cannot exceed 100 characters'}
                </div>
              )}
            </div>
            <div className="form-group">
              <label>{t('numberOfWords')}</label>
              <select value={wordCount} onChange={e => setWordCount(parseInt(e.target.value, 10))} disabled={loading}>
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <div className="learn-topic-actions">
              <button className="btn btn-primary" onClick={async () => {
                setError('');
                setLoading(true); // Show spinner immediately
                // Check topic before AI generation
                if (!isTopicSafe(topic)) {
                  setError(t('inappropriateContentDetected'));
                  setLoading(false);
                  return;
                }
                // Get the current user's access token
                const { data: { session } } = await supabase.auth.getSession();
                const accessToken = session?.access_token;
                // Check word cap before showing spinner
                const checkRes = await fetch(`${backendUrl}/usage/check-words`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    accessToken,
                    wordCount,
                    timezone: getUserTimezone(),
                    // Pass classroomId so the check uses classroom limits instead of personal limits
                    classroomId: moduleData.classroom_id || null,
                    moduleId: moduleData.id
                  })
                });
                const checkData = await checkRes.json();
                if (!checkData.canGenerate) {
                  setError(checkData.error || t('noWordsGenerated'));
                  setLoading(false);
                  return;
                }
                const currentRequestId = ++requestIdRef.current;
                try {
                  const res = await fetch(`${backendUrl}/gemini-generate-words`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      topic,
                      fromLang: moduleData.from_lang,
                      toLang: moduleData.to_lang,
                      dialect: moduleData.dialect,
                      wordCount,
                      accessToken,
                      timezone: getUserTimezone(),
                      // Pass moduleId and classroom_id for security verification
                      // Backend will verify module belongs to classroom before using classroom budget
                      moduleId: moduleData.id,
                      classroomId: moduleData.classroom_id || null,
                    })
                  });
                  const data = await res.json();
                  if (requestIdRef.current === currentRequestId) {
                    if (data.words && data.words.length > 0) {
                      await handleAIGeneratedWords(topic, data.words);
                      if (refetchUsage) refetchUsage();
                    } else if (data.error) {
                      setError(data.error);
                    } else {
                      setError(t('noWordsGenerated'));
                    }
                  }
                } catch (e) {
                  if (requestIdRef.current === currentRequestId) {
                    setError(t('failedToGenerateWords'));
                  }
                }
                if (requestIdRef.current === currentRequestId) setLoading(false);
              }} disabled={loading || !topic}>
                {loading ? t('generating') : t('generateWords')}
              </button>
              <button className="btn btn-secondary" onClick={() => { setLearnMode(null); setError(''); }} disabled={loading}>
                {t('cancel')}
              </button>
            </div>
            {error && (
              <div className="error-message">
                <div>{error}</div>
                {error.includes('Want to learn more?') && (
                  <Link
                    to="/store"
                    className="btn btn-primary"
                    style={{
                      marginTop: '0.8rem',
                      display: 'inline-block',
                      textDecoration: 'none'
                    }}
                  >
                    {t('upgradeToPremium')}
                  </Link>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default LearnTopicPage;
